import React, { FC, ReactNode } from "react";
import { Box, SxProps, Theme } from "@mui/material";
import { HAMASPIK_COLORS, HAMASPIK_FONT_BODY } from "./theme";

export type PifBadgeVariant = "neutral" | "primary" | "secondary" | "success" | "warning" | "danger" | "info";
export type PifBadgeSize = "sm" | "md" | "lg";

const PALETTES: Record<PifBadgeVariant, { bg: string; fg: string }> = {
  neutral: { bg: HAMASPIK_COLORS.neutral100, fg: HAMASPIK_COLORS.textMuted },
  primary: { bg: HAMASPIK_COLORS.orange50, fg: HAMASPIK_COLORS.orange700 },
  secondary: { bg: HAMASPIK_COLORS.brown50, fg: HAMASPIK_COLORS.brown600 },
  success: { bg: HAMASPIK_COLORS.success50, fg: HAMASPIK_COLORS.success700 },
  warning: { bg: HAMASPIK_COLORS.warning50, fg: HAMASPIK_COLORS.warning700 },
  danger: { bg: HAMASPIK_COLORS.danger50, fg: HAMASPIK_COLORS.danger700 },
  info: { bg: HAMASPIK_COLORS.info50, fg: HAMASPIK_COLORS.info700 },
};

const SIZES: Record<PifBadgeSize, { padding: string; fontSize: string }> = {
  sm: { padding: "2px 8px", fontSize: "10px" },
  md: { padding: "4px 11px", fontSize: "12px" },
  lg: { padding: "6px 14px", fontSize: "14px" },
};

interface BadgeProps {
  children: ReactNode;
  variant?: PifBadgeVariant;
  size?: PifBadgeSize;
  sx?: SxProps<Theme>;
}

const Badge: FC<BadgeProps> = ({ children, variant = "neutral", size = "md", sx }) => {
  const p = PALETTES[variant];
  const s = SIZES[size];
  return (
    <Box
      component="span"
      sx={{
        display: "inline-flex",
        alignItems: "center",
        gap: "5px",
        padding: s.padding,
        fontFamily: HAMASPIK_FONT_BODY,
        fontWeight: 700,
        fontSize: s.fontSize,
        letterSpacing: "0.02em",
        lineHeight: 1.3,
        whiteSpace: "nowrap",
        color: p.fg,
        background: p.bg,
        borderRadius: "999px",
        flex: "none",
        ...sx,
      }}
    >
      {children}
    </Box>
  );
};

export default Badge;
